import { useMsal } from "@azure/msal-react";
import DateFnsUtils from "@date-io/date-fns";
import {
    CircularProgress, TextField, Tooltip
} from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
    KeyboardDateTimePicker, MuiPickersUtilsProvider
} from "@material-ui/pickers";
import { de } from "date-fns/locale";
import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { globalStyles } from "../../assets/globalStyles";
import { selectDate } from "../../features/konfiguration/konfigurationSlice";
import { messstelle } from "../../utils/apiTypes";
import { addTimezoneOffset, subtractTimezoneOffset, toUTCDateString } from '../../utils/utils';
import { NetzwirtschaftAutocompleteMessgroese } from "./autocompleteMessgroese";
import { getUWS, onDropDownSelect, selectMesspunkte, selectMesspunktLoading, selectRekorderLoading, selectUmspannwerkLoading, selectUWs, updateField } from "./netzwirtschaftSlice";
import { useStyles } from "./netzwirtschaftStyles";
import { TabData } from "./netzwirtschaftTypes";
import { selectMessstellenLookup, getMessstellenForLookup } from '../../features/messstellenLookup/messstellenLookupSlice';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';


export interface NetzwirtschaftInputProps {
    tabData: TabData;
}
export const NetzwirtschaftInput = ({ tabData }: NetzwirtschaftInputProps): JSX.Element => {
    const dispatch = useAppDispatch();

    const { instance } = useMsal();


    useEffect(() => {
        dispatch(getUWS());
        dispatch(getMessstellenForLookup());
    }, [instance]);


    const classes = {
        ...useStyles(),
        ...globalStyles(),
    };
    const {
        datePicker,
    } = useStyles();

    const uwLoading: boolean = useAppSelector(selectUmspannwerkLoading);
    const rekorderLoading: boolean = useAppSelector(selectRekorderLoading);
    const messpunktLoading: boolean = useAppSelector(selectMesspunktLoading);
    const isUTC = useAppSelector(selectDate);
    const uwsSelectList: string[] = useAppSelector(selectUWs);
    const messpunkteSelectList: messstelle[] = useAppSelector(selectMesspunkte);

    const [openGroups, setOpenGroups] = React.useState<Array<string>>([]);
    const [selectedGroups, setSelectedGroups] = React.useState<Array<string>>([]);
    const [uwSelectInputValue, setUwSelectInputValue] = React.useState("");
    const [messpunktSelectInputValue, setMesspunktSelectInputValue,] = React.useState("");
    const [rekorderSelectInputValue, setRekorderSelectInputValue,] = React.useState("");

    const messstellenLookup = useAppSelector(selectMessstellenLookup);

    return (
        <React.Fragment>
            <Grid container spacing={1} >

                <Grid item xs={11} md={11} >
                    <FormControl className={classes.fullWidth}>
                        <Autocomplete
                            id="select-uw"
                            noOptionsText={<React.Fragment>
                                {uwLoading ? <CircularProgress style={{ padding: "5px" }} /> : "Keine Umspannwerke"}</React.Fragment>}
                            value={tabData.fields.uw}
                            onChange={(_, newValue) => {
                                dispatch(
                                    onDropDownSelect({
                                        field: "uw",
                                        value: newValue !== null ? newValue : ""
                                    })
                                );
                            }}
                            inputValue={uwSelectInputValue}
                            onInputChange={(_, newInputValue) => {
                                setUwSelectInputValue(newInputValue);
                            }}
                            options={uwsSelectList.map((x) => x)}
                            getOptionLabel={(option) => (option ? option : "")}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Umspannwerk"
                                    variant="outlined"
                                />
                            )}
                            className={classes.selectEmpty}
                        />
                    </FormControl>
                </Grid>


                <Grid item xs={11} md={11}>
                    <FormControl
                        className={classes.fullWidth}
                        disabled={tabData.fields.uw === ""}
                    >
                        <Autocomplete
                            id="select-messpunkt"
                            noOptionsText={<React.Fragment>
                                {messpunktLoading ? <CircularProgress style={{ padding: "5px" }} /> : "Keine Messpunkte"}</React.Fragment>}
                            value={tabData.fields.messpunkt}
                            onChange={(_, newValue) => {
                                dispatch(
                                    onDropDownSelect({
                                        field: "messpunkt",
                                        value: newValue !== null ? newValue : ""
                                    })
                                );
                            }}
                            inputValue={messpunktSelectInputValue}
                            onInputChange={(_, newInputValue) => {
                                setMesspunktSelectInputValue(newInputValue);
                            }}
                            options={
                                messpunkteSelectList
                                    ? messpunkteSelectList.map((x) => x.name)
                                    : []
                            }
                            getOptionLabel={(option) => {
                                const currentMessstelle = messstellenLookup.find(x => x.name == option)
                                if (currentMessstelle)
                                    return currentMessstelle.schaltfeld + " (" + currentMessstelle.name + ")";
                                return "";
                            }}
                            renderInput={(params) => (
                                <React.Fragment>
                                    <TextField
                                        {...params}
                                        label="Messpunkt"
                                        variant="outlined"
                                    />
                                </React.Fragment>

                            )}
                            renderOption={(option) => (

                                <React.Fragment>
                                    {messstellenLookup.find(x => x.name == option)?.schaltfeld + " (" + messstellenLookup.find(x => x.name == option)?.name + ")"}
                                    <div className={classes.zugeordnetStyle}>
                                        <Tooltip title={messstellenLookup.find(x => x.name == option)?.zugeordnet ? "Aktivem Messpunkt zugeordnet" : "Keinem aktiven Messpunkt zugeordnet"}>
                                            <FiberManualRecordIcon color={messstellenLookup.find(x => x.name == option)?.zugeordnet ? "secondary" : "disabled"} />
                                        </Tooltip>
                                    </div>
                                </React.Fragment>

                            )}
                            disabled={tabData.fields.uw === ""}
                            className={classes.selectEmpty}
                        />
                    </FormControl>
                </Grid>


                <Grid item xs={11} md={11}>
                    <FormControl
                        className={classes.fullWidth}
                        disabled={tabData.fields.messpunkt === ""}
                    >
                        <Autocomplete
                            id="select-rekorder"
                            noOptionsText={<React.Fragment>
                                {rekorderLoading ? <CircularProgress style={{ padding: "5px" }} /> : "Keine Rekorder"}</React.Fragment>}
                            value={tabData.fields.rekorder}
                            onChange={(_, newValue) => {
                                dispatch(
                                    onDropDownSelect({
                                        field: "rekorder",
                                        value: newValue !== null ? { beschreibung: newValue.beschreibung, hersteller: newValue.hersteller, kategorie: newValue.kategorie, klasse: newValue.klasse, messstelle: tabData.fields.messpunkt } : ""
                                    })
                                );
                                setOpenGroups([]);
                                setSelectedGroups([]);
                            }}
                            inputValue={rekorderSelectInputValue}
                            onInputChange={(_, newInputValue) => {
                                setRekorderSelectInputValue(newInputValue);
                            }}
                            options={tabData.rekorderOptions}
                            groupBy={(option) => option.beschreibung}
                            getOptionLabel={(option) =>
                                option.kategorie
                                    ? option.beschreibung + " - " + option.kategorie
                                    : ""
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Rekorder"
                                    variant="outlined"
                                />
                            )}
                            disabled={tabData.fields.messpunkt === ""}
                            className={classes.selectEmpty}
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <Grid container spacing={1}>

                <Grid item xs={11} md={11}>
                    <MuiPickersUtilsProvider
                        locale={de}
                        utils={DateFnsUtils}>
                        <KeyboardDateTimePicker
                            value={isUTC ? addTimezoneOffset(tabData.fields.startDatum) : new Date(tabData.fields.startDatum)}
                            className={datePicker}
                            format="dd.MM.yyyy HH:mm:ss"
                            label="Startzeitpunkt"
                            okLabel="OK"
                            cancelLabel="ABBRECHEN"
                            inputVariant="outlined"
                            ampm={false}
                            disableFuture={true}
                            minDate={tabData.fields.ersteDaten}
                            maxDate={
                                new Date(tabData.fields.letzteDaten).getTime() !=
                                    new Date(0).getTime()
                                    ? tabData.fields.letzteDaten
                                    : null
                            }
                            minDateMessage="Das ausgewählte Datum liegt vor dem ersten Zeitpunkt für den Daten vorliegen"
                            maxDateMessage="Das ausgewählte Datum liegt nach dem letzen Zeitpunkt für den Daten vorliegen"
                            invalidDateMessage={"Das ausgewählte Datum hat ein ungültiges Format."}
                            onChange={(event) => {
                                dispatch(
                                    updateField({
                                        field: "startDatum",
                                        value: isUTC ? subtractTimezoneOffset(new Date(event + "")) : new Date(event + "")
                                    })
                                );
                            }}
                            disabled={new Date(tabData.fields.ersteDaten).getTime() == new Date(0).getTime() && tabData.fields.rekorder.klasse != ""}
                        ></KeyboardDateTimePicker>
                    </MuiPickersUtilsProvider>
                    <Typography variant="caption">
                        {new Date(tabData.fields.ersteDaten).getTime() ==
                            new Date(0).getTime()
                            ? tabData.fields.rekorder.klasse == ""
                                ? ""
                                : "Es liegen keine Daten für diesen Rekorder vor"
                            : "Erste Daten: " + (isUTC ? toUTCDateString(new Date(tabData.fields.ersteDaten)) : new Date(tabData.fields.ersteDaten).toLocaleString())}
                    </Typography>
                </Grid>

                <Grid item xs={11} md={11}>
                    <MuiPickersUtilsProvider locale={de} utils={DateFnsUtils}>
                        <KeyboardDateTimePicker
                            value={isUTC ? addTimezoneOffset(tabData.fields.endDatum) : new Date(tabData.fields.endDatum)}
                            className={datePicker}
                            format="dd.MM.yyyy HH:mm:ss"
                            label="Endzeitpunkt"
                            inputVariant="outlined"
                            okLabel="OK"
                            cancelLabel="ABBRECHEN"
                            ampm={false}
                            disableFuture={true}
                            minDate={tabData.fields.startDatum}
                            maxDate={
                                new Date(tabData.fields.letzteDaten).getTime() !=
                                    new Date(0).getTime()
                                    ? tabData.fields.letzteDaten
                                    : null
                            }
                            minDateMessage="Das ausgewählte Datum liegt vor dem Startzeitpunkt"
                            maxDateMessage="Das ausgewählte Datum liegt nach dem letzen Zeitpunkt für den Daten vorliegen"
                            invalidDateMessage={"Das ausgewählte Datum hat ein ungültiges Format."}
                            onChange={(event) => {
                                dispatch(
                                    updateField({
                                        field: "endDatum",
                                        value: isUTC ? subtractTimezoneOffset(new Date(event + "")) : new Date(event + "")
                                    })
                                );
                            }}
                            disabled={new Date(tabData.fields.letzteDaten).getTime() == new Date(0).getTime() && tabData.fields.rekorder.klasse != ""}
                        ></KeyboardDateTimePicker>
                    </MuiPickersUtilsProvider>
                    <Typography variant="caption">
                        {new Date(tabData.fields.letzteDaten).getTime() ===
                            new Date(0).getTime()
                            ? ""
                            : "Letzte Daten: " +
                            (isUTC ? toUTCDateString(new Date(tabData.fields.letzteDaten)) : new Date(tabData.fields.letzteDaten).toLocaleString())}
                    </Typography>
                </Grid>
                <Grid container spacing={1}>

                    <NetzwirtschaftAutocompleteMessgroese
                        tabData={tabData}
                        openGroups={openGroups}
                        selectedGroups={selectedGroups}
                        setOpenGroups={setOpenGroups}
                        setSelectedGroups={setSelectedGroups} />
                </Grid>
            </Grid>
        </React.Fragment >
    );
}
